import React, { useState, useEffect, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Bell, Sun, Moon, LogOut, CheckCircle, Settings, FolderKanban, Users, Sparkles, Briefcase, BarChart3, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import UsageLimitModal from "./components/UsageLimitModal";
import { OrganizationPlanContext } from "./contexts/OrganizationPlanContext";

import { clearUser } from "./redux/authSlice";

// Import Components
import SidebarItem from "./components/Sidebar/SidebarItem";

// Import Utils
import { SIDEBAR_ITEMS, ProjectBackButton } from "./utils/constants";

// Import Pages
import Dashboard from "./pages/Dashboard";
import BugOptimizer from "./pages/BugOptimizer";
import BugReport from "./pages/BugOptimizer/BugReport";
import BugDetails from "./pages/BugOptimizer/BugDetails";
import RequirementMapper from "./pages/RequirementMapper";
import TestCases from "./pages/TestCases";
import CreateTestCase from "./pages/TestCases/CreateTestCase";
import TestCaseDetails from "./pages/TestCases/TestCaseDetails";
import ReleaseScorer from "./pages/ReleaseScorer";
import ProjectsPage from "./pages/Projects";
import SettingsPage from "./pages/Settings";
import UsageSettings from "./pages/Settings/UsageSettings";
import Api_testing from "./pages/Api_testing";
import TestExecutor from "./pages/TestExecutor";
import EditTestCase from "./pages/TestCases/EditTestCase";
import EditBug from "./pages/BugOptimizer/EditBug";
import NotificationsPage from "./pages/Notifications";
import NotFound from "./pages/NotFound";

// Import Auth Pages
import Login from "./pages/AuthPages/Login";
import AcceptInvite from "./pages/AuthPages/AcceptInvite";
import ForgotPassword from "./pages/AuthPages/ForgotPassword";

// Import Role-Specific Dashboards
import OrgAdminDashboard from "./pages/OrgAdminDashboard";
import AutomationIntelligence from "./pages/AutomationIntelligence";
// import ProjectManagerDashboard from "./pages/ProjectManagerDashboard";
// import DeveloperDashboard from "./pages/DeveloperDashboard";
// import QADashboard from "./pages/QADashboard";

// =========================================================================
// PROJECT STATE GUARD (PREVENTS MANUAL URL BYPASS WITHOUT SELECTED PROJECT)
// =========================================================================
const ProjectGuard = ({ children }) => {
  const navigate = useNavigate();
  const { currentUser, selectedProject } = useSelector((state) => state.auth);

  useEffect(() => {
    // If the user isn't an org admin and they have no project selected in Redux, 
    // bounce them back to the projects page.
    if (currentUser?.role !== "org_admin" && !selectedProject) {
      toast.warn("Session context lost. Please select a project first.", { toastId: 'missing-project' });
      navigate("/projects", { replace: true });
    }
  }, [currentUser, selectedProject, navigate]);

  // Don't render the child components which might crash expecting project data
  if (currentUser?.role !== "org_admin" && !selectedProject) {
    return null; 
  }

  return children;
};

// =========================================================================
// ROLE GUARD COMPONENT (SMART PATH FALLBACK)
// =========================================================================
const RoleGuard = ({ children, restrictedRoles = [] }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, selectedProject } = useSelector((state) => state.auth);
  const planState = useContext(OrganizationPlanContext);
  const starterBypass = planState === true;

  useEffect(() => {
    if (!currentUser || planState === null || starterBypass) return;

    // Get the user's role specifically for the current project
    const currentRole = selectedProject?.user_role || "unassigned";
    const isRestricted =
      restrictedRoles.includes(currentRole) ||
      restrictedRoles.includes(currentUser.role);

    if (isRestricted) {
      toast.error(
        "Access Denied: You do not have permission to view or edit this.",
        { toastId: "rbac-error" },
      );

      const currentPath = location.pathname;

      // Smart Path Routing
      if (currentPath.includes("/bug-optimizer/") || currentPath.includes("/edit-bug/")) {
        navigate("/bug-optimizer", { replace: true });
      } else if (currentPath.includes("/test-cases/")) {
        navigate("/test-cases", { replace: true });
      } else if (currentPath.includes("/requirement-mapper") || currentPath.includes("/test-executor")) {
        navigate(selectedProject?.project_id ? `/dashboard/${selectedProject.project_id}` : "/projects", { replace: true });
      } else {
        navigate("/projects", { replace: true });
      }
    }
  }, [currentUser, selectedProject, navigate, location.pathname, restrictedRoles, planState, starterBypass]);

  const currentRole = selectedProject?.user_role || "unassigned";
  const isRestricted =
    !starterBypass && (
      restrictedRoles.includes(currentRole) ||
      restrictedRoles.includes(currentUser?.role)
    );
  if (planState === null) return null;
  if (isRestricted) {
    return null;
  }

  return children;
};

// =========================================================================
// MAIN APP COMPONENT
// =========================================================================
function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentUser, selectedProject } = useSelector((state) => state.auth);
  const [starterBypass, setStarterBypass] = useState(null);

  useEffect(() => {
    if (!currentUser?.org_id) {
      setStarterBypass(false);
      return undefined;
    }

    let isMounted = true;
    setStarterBypass(null);
    axios.get("https://api.testoraglobal.in/api/org/plan")
      .then((response) => {
        if (isMounted) {
          setStarterBypass(response.data.starter_bypass === true);
        }
      })
      .catch((error) => {
        if (isMounted) {
          setStarterBypass(false);
          console.error("Failed to load organization plan.", error);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [currentUser?.org_id]);

  // --- UI LOGIC ---

  // 1. Pure Auth Pages (Only show Dark Mode Toggle, No Sidebar)
  const authPages = [
    "/login",
    "/accept-invite",
    "/forgot-password",
    "/",
  ];
  const isAuthPage = authPages.includes(location.pathname);

  // 2. Hide Sidebar Pages (Includes Auth Pages + Dashboards + Settings)
  const hideSidebarPages = [
    ...authPages,
    "/settings",
    "/notifications",
    "/pm-dashboard",
    "/dev-dashboard",
    "/qa-dashboard",
  ];
  const hideSidebar = hideSidebarPages.includes(location.pathname);
  const isAdminDashboardRoute = location.pathname === "/admin-dashboard" || location.pathname.startsWith("/admin-dashboard/");
  const isProjectsRoute = location.pathname === "/projects";
  const isUsageRoute = location.pathname === "/usage";

  // 3. Dynamic Sidebar Filtering
  const currentRole = selectedProject?.user_role || "unassigned";
  const hasOrgAdminAccess = currentUser?.role === "org_admin" || starterBypass === true;
  const adminSidebarItems = [
    { icon: FolderKanban, label: "Projects", key: "Admin Projects", path: "/admin-dashboard" },
    { icon: Users, label: "Users", key: "Admin Users", path: "/admin-dashboard/users" },
    { icon: Sparkles, label: "Automation Intelligence", key: "Automation Intelligence", path: "/admin-dashboard/automation-intelligence" },
  ];
  const userSidebarItems = [
    { icon: Briefcase, label: "Projects", key: "User Projects", path: "/projects" },
  ];
  const usageSidebarItem = {
    icon: BarChart3,
    label: "Usage",
    key: "Usage",
    path: "/usage",
  };
  const visibleSidebarItems = isAdminDashboardRoute || (isUsageRoute && hasOrgAdminAccess)
    ? [...adminSidebarItems, ...(hasOrgAdminAccess ? [usageSidebarItem] : [])]
    : isProjectsRoute
      ? [...userSidebarItems, ...(hasOrgAdminAccess ? [usageSidebarItem] : [])]
      : [
        ...SIDEBAR_ITEMS.filter((item) => {
    if (!starterBypass && (
      currentUser?.role === "org_admin" ||
      currentRole === "manager" ||
      currentRole === "developer"
    )) {
      const restrictedPaths = ["/requirement-mapper", "/test-executor"];
      if (restrictedPaths.includes(item.path)) {
        return false;
      }
    }
    
        return true;
      }),
        ...(hasOrgAdminAccess ? [usageSidebarItem] : []),
      ];

  // 4. Intelligent Active Route Mapping (NEW)
  const isSidebarItemActive = (itemPath) => {
    const current = location.pathname;
    
    // 1. Exact match
    if (current === itemPath) return true;
    
    // 2. Prevent parent routes from highlighting when a specific child route is active
    // (e.g. '/admin-dashboard' shouldn't highlight if we are on '/admin-dashboard/users')
    if (itemPath === "/admin-dashboard") return current === "/admin-dashboard";

    // 3. Match sub-paths naturally (e.g. /test-cases/create matches /test-cases)
    if (itemPath !== "/" && current.startsWith(`${itemPath}/`)) return true;
    
    // 4. Custom disconnected sub-routes (connects /edit-bug to Bug Optimizer)
    if (itemPath === "/bug-optimizer" && current.startsWith("/edit-bug")) return true;
    
    return false;
  };

  // State for Dark Mode
  const [darkMode, setDarkMode] = useState(true);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [usageLimitError, setUsageLimitError] = useState(null);

  useEffect(() => {
    const handleUsageLimit = (event) => {
      setUsageLimitError(event.detail || {});
    };
    window.addEventListener("testora:usage-limit", handleUsageLimit);
    return () => window.removeEventListener("testora:usage-limit", handleUsageLimit);
  }, []);

  useEffect(() => {
    if (!currentUser?.user_id || isAuthPage) return;

    const fetchNotifications = async () => {
      const token = window.localStorage.getItem("token");
      if (!token) return;

      try {
        const response = await fetch(`https://api.testoraglobal.in/api/notifications/user/${currentUser.user_id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          if (response.status === 403) {
            return;
          }
          throw new Error("Failed to fetch notifications");
        }

        const data = await response.json();
        setNotifications(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Failed to load notifications", error);
      }
    };

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [currentUser?.user_id, isAuthPage]);

  const topNotifications = notifications.slice(0, 5);
  const unreadCount = notifications.filter((item) => !item.is_read).length;

  const handleOpenNotifications = async () => {
    if (!currentUser?.user_id) return;
    setIsNotificationsOpen((prev) => !prev);
  };

  const getNotificationPath = (notification) => {
    if (notification?.target?.path) return notification.target.path;
    if (notification?.type === "bug") return "/bug-optimizer";
    if (notification?.type === "project") return "/projects";
    return "/notifications";
  };

  const handleNotificationClick = async (notification) => {
    try {
      if (!notification.is_read) {
        const token = window.localStorage.getItem("token");
        if (!token) {
          throw new Error("Authentication is required to update notifications.");
        }

        const response = await fetch(`https://api.testoraglobal.in/api/notifications/read/${notification.notification_id}`, {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          throw new Error("Failed to mark notification as read");
        }

        setNotifications((prev) => prev.map((item) =>
          item.notification_id === notification.notification_id ? { ...item, is_read: true } : item
        ));
      }
    } catch (error) {
      console.error("Failed to mark notification as read", error);
    } finally {
      setIsNotificationsOpen(false);
      navigate(getNotificationPath(notification));
    }
  };

  // Effect to update the root HTML and Body classes
  useEffect(() => {
    const root = window.document.documentElement;
    if (darkMode) {
      root.classList.add("dark");
      document.body.style.backgroundColor = "#0b0d13";
    } else {
      root.classList.remove("dark");
      document.body.style.backgroundColor = "#f8fafc";
    }
  }, [darkMode]);

  const toggleMode = () => setDarkMode(!darkMode);

  const handleLogout = () => {
    localStorage.removeItem("token");
    dispatch(clearUser());
    navigate("/login");
  };

  // Dynamic Theme Variables
  const bgColor = darkMode ? "bg-testora-dark" : "bg-gray-50";
  const textColor = darkMode ? "text-white" : "text-gray-900";
  const borderColor = darkMode ? "border-gray-800/40" : "border-gray-200";

  return (
    <OrganizationPlanContext.Provider value={starterBypass}>
      <ToastContainer />
      <UsageLimitModal
        error={usageLimitError}
        onClose={() => setUsageLimitError(null)}
        darkMode={darkMode}
      />

      <div
        className={`flex h-screen ${bgColor} ${textColor} font-sans transition-colors duration-300 overflow-hidden`}
      >
        {/* SIDEBAR */}
        {!hideSidebar && (
          <aside
            className={`${isSidebarCollapsed ? "w-16" : "w-64"} border-r ${borderColor} flex flex-col shrink-0 overflow-hidden transition-[width] duration-300 ease-in-out ${darkMode ? "bg-testora-sidebar" : "bg-white"}`}
          >
            {/* LOGO SECTION */}
            <div className={`${isSidebarCollapsed ? "p-2 flex-col gap-3" : "p-4 items-center justify-between"} text-purple-500 font-bold flex`}>
              <div className="flex items-center gap-2">
                <div className="bg-purple-500 text-white p-1 rounded-lg">
                  <CheckCircle size={18} />
                </div>
                <span className={`overflow-hidden whitespace-nowrap transition-[max-width,opacity] duration-200 ${isSidebarCollapsed ? "max-w-0 opacity-0" : "max-w-32 opacity-100 text-2xl"}`}>
                  TESTORA
                </span>
              </div>
              <button
                type="button"
                onClick={() => setIsSidebarCollapsed((collapsed) => !collapsed)}
                className={`rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-800/50 hover:text-purple-400 ${isSidebarCollapsed ? "self-center" : ""}`}
                aria-label={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                {isSidebarCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
              </button>
            </div>

            {/* PROJECT BACK BUTTON PLACED AT TOP */}
            {!isAdminDashboardRoute && !isProjectsRoute && <ProjectBackButton collapsed={isSidebarCollapsed} />}
            
            {/* NAVIGATION MENU (Filtered based on roles) */}
            <nav className={`flex-1 space-y-1 mt-2 overflow-y-auto ${isSidebarCollapsed ? "px-2" : "px-4"}`}>
              {visibleSidebarItems.map((item) => (
                <SidebarItem
                  key={item.key}
                  icon={<item.icon size={18} />}
                  label={item.label}
                  path={item.path}
                  darkMode={darkMode}
                  isComingSoon={item.isComingSoon}
                  collapsed={isSidebarCollapsed}
                  // 👉 Injecting exact active state
                  isActive={isSidebarItemActive(item.path)} 
                  active={isSidebarItemActive(item.path)} 
                />
              ))}
            </nav>

            {/* USER PROFILE SECTION */}
            <div className={`${isSidebarCollapsed ? "p-2" : "p-4"} border-t ${borderColor}`}>
              <div
                className={`flex items-center rounded-xl border ${isSidebarCollapsed ? "flex-col gap-2 p-1" : "justify-between p-3"} ${darkMode ? "bg-gray-900/30 border-gray-800/20" : "bg-gray-100 border-gray-200"}`}
              >
                <div className={`flex items-center min-w-0 ${isSidebarCollapsed ? "" : "gap-3 flex-1"}`}>
                  <div className="w-10 h-10 shrink-0 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center text-sm font-bold border border-purple-500/30">
                    {currentUser?.user_name
                      ? currentUser.user_name
                          .split(" ")
                          .map((word) => word[0])
                          .slice(0, 2)
                          .join("")
                      : "U"}
                  </div>
                  <div className={`flex-1 min-w-0 overflow-hidden transition-[width,opacity] duration-200 ${isSidebarCollapsed ? "w-0 opacity-0" : "opacity-100"}`}>
                    <p className="text-sm font-bold truncate text-gray-400 block w-full">
                      {currentUser?.user_name || "Guest User"}
                    </p>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider truncate block w-full">
                      {currentUser?.role?.replace("_", " ") || "Team Member"}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className={`shrink-0 p-1 rounded-lg ${darkMode ? "text-gray-300 hover:text-[#f43f5e] hover:bg-gray-800" : "text-gray-600 hover:text-[#f43f5e] hover:bg-gray-200"} transition cursor-pointer font-medium flex items-center justify-center`}
                  aria-label="Log out"
                  title="Log out"
                >
                  <LogOut size={16} />
                </button>
              </div>
            </div>
          </aside>
        )}

        {/* MAIN CONTENT AREA */}
        <main className="min-w-0 flex-1 overflow-y-auto flex flex-col transition-[width] duration-300 ease-in-out">
          {/* GLOBAL TOPBAR */}
          <header
            className={`flex justify-between items-center w-full p-6 sticky top-0 z-10 backdrop-blur-sm transition-colors ${darkMode ? "bg-testora-dark/90" : "bg-gray-50/90"}`}
          >
            <div className="flex items-center gap-4 ml-auto">
              {/* 1. Theme Toggle (Visible on ALL Pages) */}
              <button
                onClick={toggleMode}
                className={`p-2 rounded-lg transition-all cursor-pointer ${darkMode ? "hover:bg-gray-800 text-gray-400" : "hover:bg-gray-200 text-gray-600"}`}
              >
                {darkMode ? (
                  <Sun size={20} className="text-yellow-400" />
                ) : (
                  <Moon size={20} className="text-indigo-600" />
                )}
              </button>

              {/* ONLY Visible on Non-Auth Pages */}
              {!isAuthPage && (
                <>
                  {/* 2. Settings */}
                  <button
                    onClick={() => navigate("/settings")}
                    className={`p-2 rounded-lg transition-all cursor-pointer ${darkMode ? "hover:bg-gray-800 text-gray-400" : "hover:bg-gray-200 text-gray-600"}`}
                    title="Settings"
                  >
                    <Settings size={20} />
                  </button>

                  {/* 3. Notifications */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={handleOpenNotifications}
                      className="relative p-2 rounded-lg transition-all cursor-pointer hover:bg-gray-800 text-gray-400"
                      aria-label="View notifications"
                    >
                      <Bell size={20} className="text-gray-500 cursor-pointer hover:text-purple-500 transition-colors" />
                      {unreadCount > 0 && (
                        <span className="absolute top-1 right-1 flex h-2.5 w-2.5 items-center justify-center rounded-full bg-red-500 border-2 border-white dark:border-testora-dark"></span>
                      )}
                    </button>

                    {isNotificationsOpen && (
                      <div className={`absolute right-0 top-14 w-96 rounded-2xl border shadow-2xl z-20 ${darkMode ? "bg-[#0B1220] border-gray-800" : "bg-white border-slate-200"}`}>
                        <div className={`flex items-center justify-between border-b px-4 py-3 ${darkMode ? "border-gray-800" : "border-slate-200"}`}>
                          <p className="font-semibold">Notifications</p>
                          <span className={`text-xs ${darkMode ? "text-gray-400" : "text-slate-500"}`}>
                            {unreadCount} unread
                          </span>
                        </div>

                        <div className="max-h-80 overflow-y-auto p-2">
                          {topNotifications.length === 0 ? (
                            <div className={`rounded-xl p-4 text-sm ${darkMode ? "text-gray-400" : "text-slate-500"}`}>
                              No notifications yet.
                            </div>
                          ) : (
                            topNotifications.map((notification) => (
                              <div
                                key={notification.notification_id}
                                role="button"
                                tabIndex={0}
                                onClick={() => handleNotificationClick(notification)}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter" || event.key === " ") handleNotificationClick(notification);
                                }}
                                className={`rounded-xl border p-3 mb-2 ${
                                  darkMode
                                    ? notification.is_read
                                      ? "border-gray-800 bg-[#111827]"
                                      : "border-purple-500/30 bg-[#141b2d]"
                                    : notification.is_read
                                      ? "border-slate-200 bg-slate-50"
                                      : "border-purple-200 bg-purple-50"
                                } cursor-pointer hover:border-purple-400`}
                              >
                                <p className="text-sm font-semibold">{notification.title}</p>
                                <p className={`mt-1 text-xs ${darkMode ? "text-gray-300" : "text-slate-600"}`}>
                                  {notification.message}
                                </p>
                              </div>
                            ))
                          )}
                        </div>

                        <div className={`border-t p-3 ${darkMode ? "border-gray-800" : "border-slate-200"}`}>
                          <button
                            type="button"
                            onClick={() => {
                              setIsNotificationsOpen(false);
                              navigate("/notifications");
                            }}
                            className={`w-full rounded-xl px-3 py-2 text-sm font-medium ${darkMode ? "bg-gray-800 text-white hover:bg-gray-700" : "bg-slate-100 text-slate-800 hover:bg-slate-200"}`}
                          >
                            View All Notifications
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </header>

          {/* PAGE CONTENT */}
          <div className="flex-1">
            <Routes>
              {/* AUTH & DEFAULT ROUTING */}
              <Route path="/login" element={currentUser ? <Navigate to={hasOrgAdminAccess ? "/admin-dashboard" : "/projects"} replace /> : <Login />} />
              <Route path="/accept-invite" element={currentUser ? <Navigate to={hasOrgAdminAccess ? "/admin-dashboard" : "/projects"} replace /> : <AcceptInvite />} />
              <Route path="/forgot-password" element={currentUser ? <Navigate to={hasOrgAdminAccess ? "/admin-dashboard" : "/projects"} replace /> : <ForgotPassword />} />
              <Route path="/" element={<Navigate to={currentUser ? hasOrgAdminAccess ? "/admin-dashboard" : "/projects" : "/login"} replace />} />

              {/* PROJECTS PAGE (Strictly block Org Admins) */}
              <Route path="/projects" element={!currentUser ? <Navigate to="/login" replace /> : hasOrgAdminAccess ? <Navigate to="/admin-dashboard" replace /> : <ProjectsPage darkMode={darkMode} /> } />

              {/* ADMIN DASHBOARD (Strictly block normal users) */}
              <Route path="/admin-dashboard" element={!currentUser ? <Navigate to="/login" replace /> : hasOrgAdminAccess ? <OrgAdminDashboard darkMode={darkMode} initialTab="projects" /> : starterBypass === null ? null : <Navigate to="/projects" replace /> } />
              <Route path="/admin-dashboard/users" element={!currentUser ? <Navigate to="/login" replace /> : hasOrgAdminAccess ? <OrgAdminDashboard darkMode={darkMode} initialTab="users" /> : starterBypass === null ? null : <Navigate to="/projects" replace /> } />
              <Route path="/admin-dashboard/automation-intelligence" element={!currentUser ? <Navigate to="/login" replace /> : hasOrgAdminAccess ? <AutomationIntelligence darkMode={darkMode} /> : starterBypass === null ? null : <Navigate to="/projects" replace /> } />
              <Route
                path="/usage"
                element={
                  !currentUser
                    ? <Navigate to="/login" replace />
                    : hasOrgAdminAccess
                      ? (
                        <div className="mx-auto w-full max-w-7xl px-6 py-8 lg:px-8">
                          <UsageSettings darkMode={darkMode} />
                        </div>
                      )
                      : starterBypass === null
                        ? null
                        : <Navigate to="/projects" replace />
                }
              />

              {/* COMMON PROJECT ROUTES (Protected by ProjectGuard) */}
              <Route path="/dashboard" element={currentUser ? <ProjectGuard><Dashboard darkMode={darkMode} /></ProjectGuard> : <Navigate to="/login" replace />} />
              
              {/* Note: This specific route doesn't need ProjectGuard because it fetches the project ID from the URL param and sets it to Redux */}
              <Route path="/dashboard/:projectId" element={currentUser ? <Dashboard darkMode={darkMode} /> : <Navigate to="/login" replace />} />

              <Route path="/bug-optimizer" element={currentUser ? <ProjectGuard><BugOptimizer darkMode={darkMode} /></ProjectGuard> : <Navigate to="/login" replace />} />
              <Route path="/bug-optimizer/view/:bugId" element={currentUser ? <ProjectGuard><BugDetails darkMode={darkMode} /></ProjectGuard> : <Navigate to="/login" replace />} />
              <Route path="/test-cases" element={currentUser ? <ProjectGuard><TestCases darkMode={darkMode} /></ProjectGuard> : <Navigate to="/login" replace />} />
              <Route path="/test-cases/:testCaseId" element={currentUser ? <ProjectGuard><TestCaseDetails darkMode={darkMode} /></ProjectGuard> : <Navigate to="/login" replace />} />

              {/* RESTRICTED ROUTES (Blocked for Developers + ProjectGuard) */}
              <Route path="/bug-optimizer/createbug" element={
                currentUser ? (
                  <ProjectGuard>
                    <RoleGuard restrictedRoles={["developer", "manager", "org_admin"]}>
                      <BugReport darkMode={darkMode} />
                    </RoleGuard>
                  </ProjectGuard>
                ) : <Navigate to="/login" replace />
              } />

              <Route path="/edit-bug/:bugId" element={
                currentUser ? (
                  <ProjectGuard>
                    <RoleGuard restrictedRoles={["developer", "manager", "org_admin"]}>
                      <EditBug darkMode={darkMode} />
                    </RoleGuard>
                  </ProjectGuard>
                ) : <Navigate to="/login" replace />
              } />

              <Route path="/requirement-mapper" element={
                currentUser ? (
                  <ProjectGuard>
                    <RoleGuard restrictedRoles={["developer", "manager", "org_admin"]}>
                      <RequirementMapper darkMode={darkMode} />
                    </RoleGuard>
                  </ProjectGuard>
                ) : <Navigate to="/login" replace />
              } />

              <Route path="/test-cases/create" element={
                currentUser ? (
                  <ProjectGuard>
                    <RoleGuard restrictedRoles={["developer", "manager", "org_admin"]}>
                      <CreateTestCase darkMode={darkMode} />
                    </RoleGuard>
                  </ProjectGuard>
                ) : <Navigate to="/login" replace />
              } />

              <Route path="/test-cases/edit/:testCaseId" element={
                currentUser ? (
                  <ProjectGuard>
                    <RoleGuard restrictedRoles={["developer", "manager", "org_admin"]}>
                      <EditTestCase darkMode={darkMode} />
                    </RoleGuard>
                  </ProjectGuard>
                ) : <Navigate to="/login" replace />
              } />

              <Route path="/test-executor" element={
                currentUser ? (
                  <ProjectGuard>
                    <RoleGuard restrictedRoles={["developer", "manager", "org_admin"]}>
                      <TestExecutor darkMode={darkMode} />
                    </RoleGuard>
                  </ProjectGuard>
                ) : <Navigate to="/login" replace />
              } />

              {/* MISC ROUTES */}
              <Route path="/release-scorer" element={currentUser ? <ProjectGuard><ReleaseScorer darkMode={darkMode} /></ProjectGuard> : <Navigate to="/login" replace />} />
              <Route path="/settings" element={currentUser ? <SettingsPage darkMode={darkMode} /> : <Navigate to="/login" replace />} />
              <Route path="/notifications" element={currentUser ? <NotificationsPage darkMode={darkMode} /> : <Navigate to="/login" replace />} />
              <Route path="/apitesting" element={currentUser ? <ProjectGuard><Api_testing darkMode={darkMode} /></ProjectGuard> : <Navigate to="/login" replace />} />
              <Route path="*" element={<NotFound darkMode={darkMode} />} />
            </Routes>
          </div>
        </main>
      </div>
    </OrganizationPlanContext.Provider>
  );
}

export default App;